/**
 * Centralized permissions & role system.
 *
 * Previously, 49+ inline checks scattered across 14+ files used magic numbers
 * like `userGroups.includes(1)`. This module provides named constants and
 * helper functions so that permission logic is consistent and discoverable.
 *
 * Role IDs (from Django backend):
 *   1 = Super User
 *   2 = Admin
 *   3 = Accounting
 *   4 = HR
 *   5 = Manager
 *   6 = Regular User
 */

// ---------------------------------------------------------------------------
// Role constants
// ---------------------------------------------------------------------------

export const ROLES = {
  SUPER_USER: 1,
  ADMIN: 2,
  ACCOUNTING: 3,
  HR: 4,
  MANAGER: 5,
  REGULAR: 6,
} as const;

export type RoleId = (typeof ROLES)[keyof typeof ROLES];

/** Human-readable labels for each role, indexed by group ID. */
export const ROLE_LABELS: Record<RoleId, string> = {
  [ROLES.SUPER_USER]: 'Super User',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.ACCOUNTING]: 'Accounting',
  [ROLES.HR]: 'HR',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.REGULAR]: 'User',
};

// ---------------------------------------------------------------------------
// Permission checking helpers
// ---------------------------------------------------------------------------

/**
 * Check whether a user belongs to a specific role.
 */
export function hasRole(groups: number[] | undefined, role: RoleId): boolean {
  return groups?.includes(role) ?? false;
}

/**
 * Check whether a user belongs to ANY of the given roles.
 */
export function hasAnyRole(
  groups: number[] | undefined,
  roles: RoleId[],
): boolean {
  if (!groups) return false;
  return roles.some((role) => groups.includes(role));
}

// ---------------------------------------------------------------------------
// Convenience predicates
// ---------------------------------------------------------------------------

export function isSuperUser(groups: number[] | undefined): boolean {
  return hasRole(groups, ROLES.SUPER_USER);
}

export function isAdmin(groups: number[] | undefined): boolean {
  return hasRole(groups, ROLES.ADMIN);
}

export function isAccounting(groups: number[] | undefined): boolean {
  return hasRole(groups, ROLES.ACCOUNTING);
}

export function isHR(groups: number[] | undefined): boolean {
  return hasRole(groups, ROLES.HR);
}

export function isManager(groups: number[] | undefined): boolean {
  return hasRole(groups, ROLES.MANAGER);
}

/**
 * True if the user has either Super User or Admin role.
 * This is the most common permission check across the codebase.
 */
export function isAdminOrAbove(groups: number[] | undefined): boolean {
  return hasAnyRole(groups, [ROLES.SUPER_USER, ROLES.ADMIN]);
}

/**
 * True if the user can view financial information (Super User, Admin, or Accounting).
 */
export function canViewFinancials(groups: number[] | undefined): boolean {
  return hasAnyRole(groups, [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING]);
}

/**
 * True if the user can view all users (Super User, Admin, or Manager).
 */
export function canViewAllUsers(groups: number[] | undefined): boolean {
  return hasAnyRole(groups, [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.MANAGER]);
}

/**
 * True if the user can manage matters (Super User, Admin, or Accounting).
 */
export function canManageMatters(groups: number[] | undefined): boolean {
  return hasAnyRole(groups, [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING]);
}

// ---------------------------------------------------------------------------
// Sidebar visibility helper
// ---------------------------------------------------------------------------

/**
 * Check whether a menu item should be visible for the given user groups.
 * `visibleForGroups` is an array of role IDs that should see the item.
 * An empty / undefined array means visible to everyone.
 */
export function isMenuItemVisible(
  userGroups: number[] | undefined,
  visibleForGroups: RoleId[] | undefined,
): boolean {
  if (!visibleForGroups || visibleForGroups.length === 0) return true;
  return hasAnyRole(userGroups, visibleForGroups);
}
